import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DeleteModal } from '@app/components/shared/Modal';
import { Snackbar, useSnackbar } from '@app/components/shared/Snackbar';
import { AdminHeader } from '@app/components/shared/AdminHeader';
import { EditCustomerModal } from '@app/components/shared/EditCustomerModal';
import { initializeSalesforceConfig, getSalesforceUrl, isSalesforceConfigInitialized } from '@shared/utils/salesforceUtils';

interface Customer {
  id: number;
  sfid?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  email: string;
  phone?: string | null;
  createdDate?: string | null;
}

interface Event {
  id: number;
  title: string;
  eventDate: string;
  recipientCount: number;
  giftListSent: boolean;
  giftListNotCreated: boolean;
}

interface CustomerDetailsEventsProps {
  onLogout: () => void;
}

export function CustomerDetailsEvents({ onLogout }: CustomerDetailsEventsProps) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState<{ show: boolean; event?: Event }>({ show: false });
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [showDeleteCustomerModal, setShowDeleteCustomerModal] = useState<{ show: boolean; customer?: Customer }>({ show: false });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(false);
  const [salesforceConfigLoaded, setSalesforceConfigLoaded] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { customerId } = useParams();

  const fetchCustomerAndEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/admin/customers/${customerId}/events`, {
        credentials: 'include',
      });

      if (response.status === 401) {
        onLogout();
        navigate('/login');
        return;
      }

      const result = await response.json();
      if (result.success) {
        setCustomer(result.data.customer);
        console.log('Events fetched:', result.data.events);
        setEvents(result.data.events);
      } else {
        showSnackbar(result.error || 'Failed to fetch customer data', 'error');
      }
    } catch (err) {
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchCustomerAndEvents();
    }
    
    // Initialize Salesforce config
    initializeSalesforceConfig().then(() => {
      setSalesforceConfigLoaded(true);
    });
  }, [customerId]);

  const handleDeleteEvent = async (event: Event) => {
    setDeletingEvent(true);
    try {
      const response = await fetch(`/api/admin/events/${event.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const result = await response.json();
      if (result.success) {
        setEvents(events.filter(e => e.id !== event.id));
        setShowDeleteModal({ show: false });
        showSnackbar('Event deleted successfully!', 'success');
      } else {
        showSnackbar(result.error || 'Failed to delete event', 'error');
      }
    } catch (err) {
      showSnackbar('Network error occurred', 'error');
    } finally {
      setDeletingEvent(false);
    }
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    try {
      const response = await fetch(`/api/admin/customers/${customer.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.status === 401) {
        onLogout();
        navigate('/login');
        return;
      }

      const result = await response.json();
      if (result.success) {
        // Navigate back to customer list after deletion
        showSnackbar('Customer deleted successfully!', 'success');
        navigate('/admin');
      } else {
        showSnackbar(result.error || 'Failed to delete customer', 'error');
      }
    } catch (err) {
      showSnackbar('Network error occurred', 'error');
    }
  };


  const handleReInviteCustomer = async (customer: Customer) => {
    try {
      setSendingInvite(true);
      const response = await fetch(`/api/admin/customers/${customer.id}/re-invite`, {
        method: 'POST',
        credentials: 'include',
      });

      const result = await response.json();
      if (result.success) {
        showSnackbar('Re-invite email sent successfully!', 'success');
      } else {
        showSnackbar(result.error || 'Failed to send re-invite email', 'error');
      }
    } catch (err) {
      showSnackbar('Network error occurred', 'error');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleImpersonate = async (customer: Customer) => {
    try {
      setImpersonating(true);
      const response = await fetch('/api/admin/impersonate-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accountId: customer.id }),
        credentials: 'include',
      });

      const result = await response.json();
      if (result.success) {
        // Open customer portal in a new tab
        window.open('/customer', '_blank');
        showSnackbar('Impersonation successful! Customer portal opened in new tab.', 'success');
      } else {
        showSnackbar(result.error || 'Failed to impersonate customer', 'error');
      }
    } catch (err) {
      showSnackbar('Network error occurred', 'error');
    } finally {
      setImpersonating(false);
    }
  };

  const getStatusBadge = (event: Event) => {
    if (event.giftListSent) {
      return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">Gift List Sent</span>;
    } else if (event.giftListNotCreated) {
      return <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">Gift List not created</span>;
    } else {
      return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">Gift List not created</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading customer details...</div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-red-600">Customer not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader onLogout={onLogout} />

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-6 cursor-pointer"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Customers List
        </button>


        {/* Customer Header Card */}
        <div className="bg-white shadow-sm rounded-lg mb-6 border border-gray-300 border-l-4 border-l-gray-400">
          <div className="p-6">
            {/* First Row: Customer Name and Event Count */}
            <div className="flex justify-between items-center mb-2">
              <div>
                <span className="text-sm text-gray-600">Customer: </span>
                <span className="text-sm font-medium text-gray-900">
                  {customer.name || `${customer.firstName} ${customer.lastName}`}
                </span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {events.length} Events
              </span>
            </div>
            
            {/* Second Row: Email and Phone */}
            <div className="flex justify-between items-center mb-2">
              <div>
                <span className="text-sm text-gray-600">Email: </span>
                <span className="text-sm text-gray-900">{customer.email}</span>
              </div>
              <div>
                <span className="text-sm text-gray-600">Phone: </span>
                <span className="text-sm text-gray-900">{customer.phone || 'Not provided'}</span>
              </div>
            </div>

            {/* Third Row: Salesforce Link */}
            <div className="flex justify-end items-center mb-4">
              <div>
                {customer.sfid && salesforceConfigLoaded ? (
                  <a
                    href={getSalesforceUrl(customer.sfid)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline text-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Salesforce link
                  </a>
                ) : customer.sfid ? (
                  <span className="text-gray-400 text-sm">Loading...</span>
                ) : (
                  <span className="text-gray-400 text-sm">Salesforce link</span>
                )}
              </div>
            </div>
            
            {/* Fourth Row: Action Buttons */}
            <div className="border-t border-gray-200 pt-4 flex justify-between items-center">
              <div className="flex items-center space-x-6">
                <button 
                  onClick={() => handleReInviteCustomer(customer)}
                  disabled={sendingInvite}
                  className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm">{sendingInvite ? 'Sending...' : 'Re-invite customer'}</span>
                </button>

                <button 
                  onClick={() => setEditingCustomer(customer)}
                  className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 cursor-pointer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="text-sm">Edit customer</span>
                </button>

                <button 
                  onClick={() => handleImpersonate(customer)}
                  disabled={impersonating}
                  className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-sm">{impersonating ? 'Impersonating...' : 'Impersonate'}</span>
                </button>
                
                <button 
                  onClick={() => setShowDeleteCustomerModal({ show: true, customer })}
                  className="flex items-center space-x-1 text-gray-600 hover:text-red-600 cursor-pointer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="text-sm">Delete customer</span>
                </button>
              </div>
              
              <button 
                onClick={() => {
                  navigate(`/admin/events/new?customerId=${customer.id}`);
                }}
                className="bg-black text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer"
              >
                + Add New Event
              </button>
            </div>
          </div>
        </div>


        <div className="bg-white shadow-sm rounded-lg">

          {/* Events Section */}
          <div className="px-6 py-4">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-blue-600 font-medium text-lg">
                  {(customer.firstName?.[0] || customer.name?.[0] || '?').toUpperCase()}
                </span>
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Events:</h2>
            </div>

            {events.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Add New Event Card - shown when no events */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
                  <button 
                    onClick={() => {
                      navigate(`/admin/events/new?customerId=${customer?.id}`);
                    }}
                    className="text-gray-500 hover:text-gray-700 text-center cursor-pointer"
                  >
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium">Add New Event</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event) => (
                  <div 
                    key={event.id} 
                    onClick={() => navigate(`/admin/events/${event.id}`)}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  >
                    <div className="mb-3">
                      {getStatusBadge(event)}
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{event.title}</h3>
                    <p className="text-sm text-gray-600 mb-1">
                      {event.eventDate ? new Date(event.eventDate).toLocaleDateString('en-US', { 
                        month: '2-digit',
                        day: '2-digit', 
                        year: 'numeric',
                        timeZone: 'UTC'
                      }) : 'No date set'}
                    </p>
                    <p className="text-sm text-gray-600 mb-4 italic">
                      {event.recipientCount || 0} Recipient{(event.recipientCount || 0) !== 1 ? 's' : ''}
                    </p>

                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-4">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDeleteModal({ show: true, event });
                          }}
                          className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 cursor-pointer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span className="text-xs">Delete</span>
                        </button>

                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Implement edit event functionality
                            console.log('Edit event:', event);
                          }}
                          className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 cursor-pointer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span className="text-xs">Edit event</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add New Event Card */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
                  <button 
                    onClick={() => {
                      navigate(`/admin/events/new?customerId=${customer?.id}`);
                    }}
                    className="text-gray-500 hover:text-gray-700 text-center cursor-pointer"
                  >
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium">Add New Event</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Edit Customer Modal */}
      <EditCustomerModal
        isOpen={!!editingCustomer}
        customer={editingCustomer}
        onClose={() => setEditingCustomer(null)}
        onSuccess={(updatedCustomer) => {
          setCustomer(updatedCustomer);
          setEditingCustomer(null);
        }}
        onLogout={onLogout}
        showSnackbar={showSnackbar}
      />

      {/* Delete Customer Modal */}
      <DeleteModal
        isOpen={showDeleteCustomerModal.show && !!showDeleteCustomerModal.customer}
        onClose={() => setShowDeleteCustomerModal({ show: false })}
        onConfirm={() => showDeleteCustomerModal.customer && handleDeleteCustomer(showDeleteCustomerModal.customer)}
        title="Do you want to delete this Customer?"
        message="This action will permanently delete the client and all associated events. This cannot be undone."
      />

      {/* Delete Event Modal */}
      <DeleteModal
        isOpen={showDeleteModal.show && !!showDeleteModal.event}
        onClose={() => !deletingEvent && setShowDeleteModal({ show: false })}
        onConfirm={() => showDeleteModal.event && handleDeleteEvent(showDeleteModal.event)}
        title="Do you want to delete this Event?"
        message="Sed sagittis purus eu gravida. Venenatis est eros lacus elementum sodales orci. Fermentum turpis ut consequat ac. Eget sagittis amet sed."
        isDeleting={deletingEvent}
      />

      {/* Snackbar */}
      <Snackbar
        message={snackbar.message}
        type={snackbar.type}
        isVisible={snackbar.isVisible}
        onClose={hideSnackbar}
      />
    </div>
  );
}